import { GetShopsConfigurationList } from '@/composables/shop/ShopsConfiguration';
import { Logout, UserState } from '@/composables/User';
import fetchShopInformation from '@/composables/shop/Shops';
import { fetchMigrationV5Status } from '@/composables/migrationV5';
import * as semver from 'semver';

export interface EcommerceModule {
  id: string;
  name: string;
  label: string;
  image: string;
  order: number;
  disabled?: boolean;
  downloadLink?: string | null;
  downloadInstructions: string[];
  installationInstructions: string[];
}

const ecommerceModules: Record<string, EcommerceModule> = {
  prestashop: {
    id: 'prestashop',
    name: 'Prestashop',
    label: 'PrestaShop',
    image: '/images/ecommerce-solutions/prestashop.svg',
    order: 1,
    downloadLink: 'https://media.shopimind.io/modules/v5/prestashop/shopimind.zip',
    downloadInstructions: [
      'complete.connectShop.installModule.downloadInstructions.prestashop.one',
      'complete.connectShop.installModule.downloadInstructions.prestashop.two',
      'complete.connectShop.installModule.downloadInstructions.prestashop.three',
    ],
    installationInstructions: [
      'complete.connectShop.connectModule.installationInstructions.prestashop.one',
    ],
  },
  woocommerce: {
    id: 'woocommerce',
    name: 'Woocommerce',
    label: 'WooCommerce',
    image: '/images/ecommerce-solutions/woocommerce.svg',
    order: 2,
    downloadLink: 'https://media.shopimind.io/modules/v5/woocommerce/shopimind.zip',
    downloadInstructions: [],
    installationInstructions: [
      'complete.connectShop.connectModule.installationInstructions.woocommerce.one',
      'complete.connectShop.connectModule.installationInstructions.woocommerce.two',
    ],
  },
  magento: {
    id: 'magento',
    name: 'Magento',
    label: 'Magento 1 / OpenMage',
    image: '/images/ecommerce-solutions/magento.svg',
    order: 3,
    downloadLink: 'https://media.shopimind.io/modules/v5/magento/shopimind.zip',
    downloadInstructions: [
      'complete.connectShop.installModule.downloadInstructions.magento.one',
      'complete.connectShop.installModule.downloadInstructions.magento.two',
    ],
    installationInstructions: [
      'complete.connectShop.connectModule.installationInstructions.magento.one',
      'complete.connectShop.connectModule.installationInstructions.magento.two',
    ],
  },
  magento2: {
    id: 'magento2',
    name: 'Magento 2',
    label: 'Magento 2',
    image: '/images/ecommerce-solutions/magento2.svg',
    order: 4,
    downloadLink: null,
    downloadInstructions: [],
    installationInstructions: [
      'complete.connectShop.connectModule.installationInstructions.magento2.one',
      'complete.connectShop.connectModule.installationInstructions.magento2.two',
      'complete.connectShop.connectModule.installationInstructions.magento2.three',
      'complete.connectShop.connectModule.installationInstructions.magento2.four',
      'complete.connectShop.connectModule.installationInstructions.magento2.five',
    ],
  },
  thelia: {
    id: 'thelia',
    name: 'Thelia (Bêta)',
    label: 'Thelia',
    image: '/images/ecommerce-solutions/thelia.svg',
    order: 5,
    downloadLink: null,
    downloadInstructions: [],
    installationInstructions: [
      'complete.connectShop.connectModule.installationInstructions.thelia.one',
      'complete.connectShop.connectModule.installationInstructions.thelia.two',
    ],
  },
  shopify: {
    id: 'shopify',
    name: 'Shopify',
    label: 'Shopify',
    image: '/images/ecommerce-solutions/shopify.svg',
    order: 6,
    disabled: true,
    downloadInstructions: [],
    installationInstructions: [],
  },
  other: {
    id: 'other',
    name: 'Autre',
    label: 'Autre',
    image: '/images/ecommerce-solutions/none.svg',
    order: 7,
    disabled: true,
    downloadInstructions: [],
    installationInstructions: [],
  },
};

export const getEcommerceModule = (ecommerce: string) => ecommerceModules[ecommerce];

export const getEcommerceModulesList = (): EcommerceModule[] => Object.values(ecommerceModules)
  .slice()
  .sort((a, b) => a.order - b.order);

export const checkIfDbCreated = async () => {
  try {
    const migration_pending = await GetShopsConfigurationList({
      shopId: UserState.activeShop?.id ?? 0,
      key: 'migration_pending',
      fields: 'value',
      limit: 0,
      offset: 0,
    });
    if (migration_pending === null) {
      await Logout();
      return true;
    }
    return migration_pending && migration_pending.length && migration_pending[0].value && migration_pending[0].value === '0';
  } catch (e) {
    await Logout();
  }
  return true;
};

/**
 * Returns `true` when the regular app can be served — i.e. the shop is
 * NOT actively going through the V4 → V5 platform migration. Returns
 * `false` when the backend reports a state that requires the user to
 * be on the `/migration-v5/` page.
 *
 *   none           ⇢ true (no migration concept)
 *   invited        ⇢ true (V4 UI shows a CTA; user hasn't opted in,
 *                    do NOT hijack their navigation)
 *   in_progress    ⇢ false (redirect — user is in the wizard)
 *   data_migrated  ⇢ false (redirect — transient, the wait step has to
 *                    call /finalize to seed the V5 side; if we let the
 *                    user through to the dashboard now they'd hit a
 *                    half-seeded shop)
 *   finalized      ⇢ true (officially V5, let them through)
 *
 * Fail-open semantics: a transient API error returns `true` so a 5xx
 * never traps a user on /migration-v5/.
 */
export const checkPlatformMigrationV5 = async (): Promise<boolean> => {
  try {
    const status = await fetchMigrationV5Status(UserState.activeShop?.id ?? 0);
    // The user must stay on /migration-v5/ for the whole "actively
    // migrating" span: from opting in (in_progress) through the ZF2
    // data migration (ready_to_migrate_data, data_migration_in_progress)
    // until data is migrated and the front finalizes (data_migrated).
    // none / invited / can_invite / finalized + the ZF2 first-migration
    // states let the user through (handled by V4 modal or already done).
    const waitingStates = [
      'in_progress',
      'ready_to_migrate_data',
      'data_migration_in_progress',
      'data_migrated',
    ];
    return !waitingStates.includes(status);
  } catch (e) {
    return true;
  }
};

export const checkIfShopConnected = async (): Promise<boolean> => {
  const shopsInfosData = await fetchShopInformation({ shopId: UserState.activeShop?.id ?? 0 });
  console.log(shopsInfosData);
  return Boolean(
    shopsInfosData?.urlClient !== '' && shopsInfosData?.currentBuild !== 0 && shopsInfosData?.currentVersion && shopsInfosData?.currentVersion !== ''
    && semver.gte(shopsInfosData?.currentVersion, '5.0.0'),
  );
};

export const getShopConfiguredData = async (): Promise<boolean> => {
  const shopsInfosData = await fetchShopInformation({ shopId: UserState.activeShop?.id ?? 0 });
  return Boolean(
    shopsInfosData?.urlClient !== '' && shopsInfosData?.currentBuild !== 0 && shopsInfosData?.currentVersion && shopsInfosData?.currentVersion !== ''
    && semver.gte(shopsInfosData?.currentVersion, '3.0.0'),
  );
};
